import { useEffect, useRef, useState } from "react";
import { CalendarHeart, ChevronLeft, ChevronRight, Heart, PoundSterling, Smile, User } from "lucide-react";
import { BUDGETS, OCCASIONS, RECIPIENTS, VIBES, type Budget } from "../../data/presents";
import { affiliateHref, openShop, useGiftCatalog } from "../../lib/giftCatalog";
import { findPresents, type ScoredGift } from "../../engine/presents";
import { useInstant } from "../../hooks/useInstant";
import { enabledOnly, useSiteSettings } from "../../lib/siteSettings";
import { useData } from "../../context/DataContext";
import { CountdownDots, StageHeader } from "../ua/Stage";
import { Snowflake } from "lucide-react";

function Row({ icon: Icon, label, value, onChange, options, testId }: { icon: any; label: string; value: string; onChange: (v: string) => void; options: { id: string; label: string }[]; testId: string }) {
  return (
    <label className="grid grid-cols-[7.5rem_1fr] items-center gap-2 rounded-xl border border-[#F2D9B8] bg-white px-3 py-2">
      <span className="flex items-center gap-2 text-sm font-semibold text-[#6b3a12]"><Icon className="h-4 w-4 text-[#FF5D1F]" />{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full rounded-lg border border-[#F2D9B8] bg-[#FFFBF2] px-2 py-1.5 text-sm font-bold text-ink outline-none focus:border-[#FF5D1F]">
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
    </label>
  );
}

interface Initial { recipient: string; budget: Budget; vibe: string }

export function PresentPanel({ big = false, initial, autoRun = false }: { big?: boolean; initial?: Initial; autoRun?: boolean }) {
  const settings = useSiteSettings();
  const festive = settings?.theme === "christmas";
  const recipients = enabledOnly(RECIPIENTS, settings, "recipients");
  const vibes = enabledOnly(VIBES, settings, "vibes");
  const occasions = enabledOnly(OCCASIONS, settings, "occasions");
  const [rawRecipient, setRecipient] = useState(initial?.recipient || "parent");
  const [budget, setBudget] = useState<Budget>(initial?.budget || "20-50");
  const [rawVibe, setVibe] = useState(initial?.vibe || "funny");
  const [rawOccasion, setOccasion] = useState("any");
  const recipient = recipients.some((r) => r.id === rawRecipient) ? rawRecipient : recipients[0]?.id || rawRecipient;
  const vibe = vibes.some((v) => v.id === rawVibe) ? rawVibe : "any";
  const occasion = occasions.some((o) => o.id === rawOccasion) ? rawOccasion : "any";
  const touched = useRef(false);
  const pickOccasion = (v: string) => { touched.current = true; setOccasion(v); if (v === "secret-santa") setBudget("under-20"); };
  useEffect(() => {
    if (initial || touched.current || !settings) return;
    const def = settings.disabled_options?.occasions.includes(settings.default_occasion) ? "any" : settings.default_occasion;
    setOccasion(def);
    if (def === "secret-santa") setBudget("under-20");
  }, [settings, initial]);
  const ran = useRef(false);
  const { gifts: allGifts, error } = useGiftCatalog();
  const off = settings?.disabled_options;
  const allOff = (tags: string[] | undefined, dis: string[] | undefined, total: number) => !!tags?.length && !!dis?.length && dis.length < total && tags.every((t) => dis.includes(t));
  const gifts = allGifts && (off ? allGifts.filter((g) => !allOff(g.recipients, off.recipients, RECIPIENTS.length) && !allOff(g.vibes, off.vibes, VIBES.length) && !allOff(g.occasions, off.occasions, OCCASIONS.length)) : allGifts);
  const [list, setList] = useState<ScoredGift[]>([]);
  const [i, setI] = useState(0);
  const [none, setNone] = useState(false);
  const { phase, go } = useInstant<{ result: string }>();
  const { isFavourite, toggleFavourite } = useData();
  const who = RECIPIENTS.find((r) => r.id === recipient)?.label.toLowerCase();
  const title = `Present for your ${who}`;

  const find = () => {
    if (!gifts?.length) return;
    const input = { recipient, budget, vibes: vibe === "any" ? [] : [vibe], occasion: occasion === "any" ? undefined : occasion };
    const shown = list.map((g) => g.name);
    let next = findPresents(input, 5, gifts, shown);
    if (!next.some((g) => g.has_link)) {
      const keepLinked = findPresents(input, 5, gifts, list.filter((g) => !g.has_link).map((g) => g.name));
      if (keepLinked.some((g) => g.has_link)) next = keepLinked;
    }
    if (next.length < 5 && shown.length) next = findPresents(input, 5, gifts, list[i] ? [list[i].name] : []);
    setList(next);
    setI(0);
    if (!next.length) { setNone(true); return; }
    setNone(false);
    setI(0);
    go({ result: next[0].name }, { kind: "present", title, category: "presents" });
  };
  useEffect(() => {
    if (autoRun && gifts && !ran.current) {
      ran.current = true;
      find();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRun, gifts]);
  const gift = list[i];
  const entry = gift ? { kind: "present", title, result: gift.name, category: "presents" } : null;

  return (
    <div className={`flex flex-col overflow-hidden rounded-[22px] border-[3px] ua-tile ${festive ? "border-[#0B3D2E] bg-[#FFF3E6]" : "border-[#FFC15E] bg-[#FFF3D6]"}`} data-testid="present-panel" data-theme={festive ? "christmas" : "default"}>
      <div className={`relative overflow-hidden px-3 py-3 ${festive ? "bg-[#C8102E]" : "bg-[#1a1006]"}`}>
        {festive && <><Snowflake className="absolute -left-2 -top-2 h-10 w-10 text-white/20" /><Snowflake className="absolute -right-1 bottom-0 h-8 w-8 text-white/20" /></>}
        <StageHeader title={festive ? "What Christmas present should I buy?" : "What present should I buy?"} />
      </div>
      <div className="space-y-2 p-3">
        <Row icon={User} label="Who's it for?" value={recipient} onChange={setRecipient} options={recipients} testId="present-recipient-select" />
        <Row icon={PoundSterling} label="Budget?" value={budget} onChange={(v) => setBudget(v as Budget)} options={BUDGETS} testId="present-budget-select" />
        <Row icon={Smile} label="Vibe?" value={vibe} onChange={setVibe} options={[{ id: "any", label: "Surprise me" }, ...vibes]} testId="present-vibe-select" />
        <Row icon={CalendarHeart} label="Occasion?" value={occasion} onChange={pickOccasion} options={[{ id: "any", label: "Any occasion" }, ...occasions]} testId="present-occasion-select" />
        <button className={`ua-btn w-full py-2.5 text-lg ${festive ? "!bg-[#1F8A4C] !text-white" : ""}`} onClick={find} disabled={phase === "counting" || !gifts?.length} data-testid="present-find-button">
          {!gifts ? (error ? "Couldn't load ideas — refresh" : "Loading ideas…") : phase === "counting" ? "Hunting…" : list.length ? "Find another" : "Find my present"}
        </button>
      </div>
      <div className={`mx-3 mb-3 flex flex-1 flex-col rounded-2xl border-[3px] border-[#FFC15E] bg-[#FFE9B8] p-3 ${big ? "min-h-[16rem]" : "min-h-[11rem]"}`} data-testid="present-results">
        <span className="ua-highlight mx-auto text-base">DITHER says…</span>
        {phase === "counting" && <div className="mt-3 rounded-xl bg-[#1a1006] p-2"><CountdownDots running done={false} duration={950} /></div>}
        {phase === "done" && gift && !none && (
          <div className="mt-2 flex flex-1 items-center gap-2">
            <button aria-label="Previous idea" disabled={i === 0} onClick={() => setI(i - 1)} className="rounded-full p-1 disabled:opacity-30" data-testid="present-prev-button"><ChevronLeft className="h-7 w-7" strokeWidth={3} /></button>
            <div className="flex-1 text-center" data-testid="present-result-card">
              {gift.image_url
                ? <img src={gift.image_url} alt={gift.name} loading="lazy" className="mx-auto h-20 w-20 rounded-xl border-2 border-[#FFC15E] bg-white object-contain" data-testid="present-result-image" />
                : <img src="/icons/gift.png" alt="Wrapped gift box" className="mx-auto h-16 w-16 object-contain" />}
              <p className="font-comic text-xl leading-tight text-ink [overflow-wrap:anywhere]" data-testid="present-result-name">{gift.name}</p>
              <p className="text-xs font-semibold text-[#6b3a12]">{gift.why}{gift.why && " · "}{gift.price != null ? <span className="font-sans font-black" data-testid="present-result-price">£{gift.price.toFixed(2).replace(/\.00$/, "")}</span> : BUDGETS.find((b) => b.id === gift.budget)?.label}</p>
              {!(gift.has_link && gift.id) && (
                <div className="mt-1.5" data-testid="present-inspiration">
                  <span className="inline-block rounded-full border-2 border-[#8a5a2b] bg-white px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-[#8a5a2b]" data-testid="present-inspiration-label">Gift inspiration</span>
                  <p className="mt-0.5 text-[11px] font-semibold italic text-[#8a5a2b]" data-testid="present-inspiration-message">We're finding somewhere lovely to buy this.</p>
                </div>
              )}
              {gift.has_link && gift.id && (
                <a href={affiliateHref(gift.id)} onClick={(e) => openShop(e, affiliateHref(gift.id!))} target="_blank" rel="sponsored nofollow noopener" className="mt-1.5 inline-block rounded-full border-[2.5px] border-[#1a1006] bg-[#FFE14D] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-[#1a1006] shadow-[0_2px_0_#1a1006] transition-transform hover:-translate-y-0.5" data-testid="present-shop-link">View at {gift.retailer || "shop"} →</a>
              )}
              <div className="mt-1 flex items-center justify-center gap-2">
                <span className="text-[11px] font-extrabold text-[#8a5a2b]" data-testid="present-result-index">{i + 1} / {list.length}</span>
                <button aria-label="Save gift" onClick={() => entry && toggleFavourite(entry)} className="rounded-full bg-white p-1.5" data-testid="present-save-button">
                  <Heart className={`h-4 w-4 ${entry && isFavourite(entry) ? "fill-pop-pink text-pop-pink" : ""}`} strokeWidth={2.5} />
                </button>
              </div>
            </div>
            <button aria-label="Next idea" disabled={i >= list.length - 1} onClick={() => setI(i + 1)} className="rounded-full p-1 disabled:opacity-30" data-testid="present-next-button"><ChevronRight className="h-7 w-7" strokeWidth={3} /></button>
          </div>
        )}
        {none && phase !== "counting" && <p className="m-auto text-center text-sm font-bold text-[#8a5a2b]" data-testid="present-no-results">No ideas match all your picks yet — try another budget, vibe or occasion.</p>}
        {phase === "idle" && !none && <p className="m-auto text-center text-sm font-bold text-[#8a5a2b]">Pick who, budget, vibe & occasion — {gifts ? `${gifts.length} curated ideas ready.` : "loading ideas…"}</p>}
      </div>
    </div>
  );
}
